import { fieldToDatabase } from '@databases/collab/convert/to-database';
import { type YDatabase } from '@databases/collab/y-database';
import { FieldType } from '@databases/model/field';
import { type FieldSdkContract } from '@databases/sdk-types';
import { DatabaseFieldType } from '@databases/types/database';
import { err, ok, type Result } from 'neverthrow';
import { v4 as uuidv4 } from 'uuid';

import { pickNewColor } from '../domain/field/utils/get-new-color-suggestions';
import {
  applyDescriptionDeltaSchema,
  applyNameDeltaSchema,
  getOrCreateSelectOptionSchema,
  reorderSchema,
  removeFieldSchema,
  setCloudIdSchema,
  setDefaultEnabledSchema,
  setDependentFieldSchema,
  setDescriptionSchema,
  setMultipleSchema,
  setNameSchema,
  setNumberFormatSchema,
  setPrefixOrSuffixSchema,
  setShowFieldDescriptionSchema,
  setTypeSchema,
  setWidthSchema,
  type ApplyDescriptionDeltaInput,
  type ApplyNameDeltaInput,
  type GetOrCreateSelectOptionInput,
  type ReorderInput,
  type RemoveFieldInput,
  type SetCloudIdInput,
  type SetDefaultEnabledInput,
  type SetDependentFieldInput,
  type SetDescriptionInput,
  type SetMultipleInput,
  type SetNameInput,
  type SetNumberFormatInput,
  type SetPrefixOrSuffixInput,
  type SetShowFieldDescriptionInput,
  type SetTypeInput,
  type SetWidthInput,
} from '../domain/fields/schemas';
import { assertFieldExists } from '../domain/guards/assert-field-exists';
import { assertFieldIsType } from '../domain/guards/assert-field-is-type';
import { assertFieldNameUnique } from '../domain/guards/assert-field-name-unique';
import { assertIndexInRange } from '../domain/guards/assert-index-in-range';
import { assertNotLocked } from '../domain/guards/assert-not-locked';
import { parse, tryMutate } from '../domain/result';
import { addField } from '../handlers/field/add-field';
import { deleteField } from '../handlers/field/delete-field';
import { reorderField } from '../handlers/field/reorder-field';
import { setFieldDescription } from '../handlers/field/set-field-description';
import { setFieldId } from '../handlers/field/set-field-id';
import { setFieldName } from '../handlers/field/set-field-name';
import { setFieldWidth } from '../handlers/field/set-field-width';
import { setShowFieldDescription } from '../handlers/field/set-show-field-description';
import { setCloudId } from '../handlers/field-settings/set-cloud-id';
import { setDefaultEnabled } from '../handlers/field-settings/set-default-enabled';
import { setFieldSettingsNumberFormat } from '../handlers/field-settings/set-field-settings-number-format';
import { setFieldSettingsPrefixOrSuffixField } from '../handlers/field-settings/set-field-settings-prefix-or-suffix-field';
import { setFieldType } from '../handlers/field-settings/set-field-type';
import { setIsMulti } from '../handlers/field-settings/set-is-multi';
import { setSelectOptions } from '../handlers/field-settings/set-select-options';
import { MultiValueChangeType } from '../handlers/utils/collab-utils';
import { type DatabaseField, type SdkError } from '../types';

/**
 * Public API for reading and mutating database fields (columns).
 *
 * Every mutation runs a three-step pipeline:
 *   1. **Zod validation** — rejects malformed input → `VALIDATION_ERROR`
 *   2. **Business rules** — rejects invalid document state → `STATE_ERROR`
 *      (DATABASE_LOCKED, FIELD_NOT_FOUND, INVALID_FIELD_TYPE, etc.)
 *   3. **Handler mutation** — delegates to collab; only truly unexpected
 *      throws surface as `INTERNAL_ERROR`
 */
export class FieldSdk implements FieldSdkContract {
  constructor(private readonly ydb: YDatabase) {}

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  add(field: DatabaseField<DatabaseFieldType>, index?: number): Result<void, SdkError> {
    return assertNotLocked(this.ydb)
      .andThen(() =>
        index != null ? assertIndexInRange(index, this.ydb.fields.length) : ok(undefined)
      )
      .andThen(() => assertFieldNameUnique(this.ydb, field.name))
      .andThen(() =>
        tryMutate(() => {
          addField(this.ydb, { field, index });
        })
      );
  }

  remove(input: RemoveFieldInput): Result<void, SdkError> {
    return parse(removeFieldSchema, input).andThen(({ fieldId }) =>
      assertNotLocked(this.ydb)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen(() => tryMutate(() => deleteField(this.ydb, fieldId)))
    );
  }

  setName(input: SetNameInput): Result<void, SdkError> {
    return parse(setNameSchema, input).andThen(({ fieldId, name }) =>
      assertNotLocked(this.ydb)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen((field) =>
          assertFieldNameUnique(this.ydb, name, fieldId).map(() => field)
        )
        .andThen((field) =>
          tryMutate(() => {
            setFieldName(this.ydb, fieldId, [
              { delete: field.name.length },
              { insert: name },
            ]);
          })
        )
    );
  }

  setWidth(input: SetWidthInput): Result<void, SdkError> {
    return parse(setWidthSchema, input).andThen(({ fieldId, width }) =>
      assertNotLocked(this.ydb)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen(() => tryMutate(() => setFieldWidth(this.ydb, fieldId, width)))
    );
  }

  reorder(input: ReorderInput): Result<void, SdkError> {
    return parse(reorderSchema, input).andThen(({ fieldId, index }) =>
      assertNotLocked(this.ydb)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen(() => assertIndexInRange(index, this.ydb.fields.length - 1))
        .andThen(() => {
          const currentIndex = this.ydb.fields.indexOf(fieldId);
          // addBefore removes the item first, shifting subsequent indices down by one.
          // Compensate for forward moves so the item lands at the caller's intended position.
          const adjustedIndex = index > currentIndex ? index + 1 : index;

          return tryMutate(() =>
            reorderField(this.ydb, { fieldId, newIndex: adjustedIndex })
          );
        })
    );
  }

  setDescription(input: SetDescriptionInput): Result<void, SdkError> {
    return parse(setDescriptionSchema, input).andThen(({ fieldId, description }) =>
      assertNotLocked(this.ydb)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen((field) => {
          const currentLength = field.description?.length ?? 0;
          const delta =
            currentLength > 0
              ? [{ delete: currentLength }, { insert: description }]
              : [{ insert: description }];

          return tryMutate(() => setFieldDescription(this.ydb, fieldId, delta));
        })
    );
  }

  applyNameDelta(input: ApplyNameDeltaInput): Result<void, SdkError> {
    return parse(applyNameDeltaSchema, input).andThen(({ fieldId, delta }) =>
      assertNotLocked(this.ydb)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen(() => tryMutate(() => setFieldName(this.ydb, fieldId, delta)))
    );
  }

  applyDescriptionDelta(input: ApplyDescriptionDeltaInput): Result<void, SdkError> {
    return parse(applyDescriptionDeltaSchema, input).andThen(({ fieldId, delta }) =>
      assertNotLocked(this.ydb)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen(() => tryMutate(() => setFieldDescription(this.ydb, fieldId, delta)))
    );
  }

  setMultiple(input: SetMultipleInput): Result<void, SdkError> {
    return parse(setMultipleSchema, input).andThen(({ fieldId, multiple }) =>
      assertNotLocked(this.ydb)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen(() => tryMutate(() => setIsMulti(this.ydb, fieldId, multiple)))
    );
  }

  setDefaultEnabled(input: SetDefaultEnabledInput): Result<void, SdkError> {
    return parse(setDefaultEnabledSchema, input).andThen(({ fieldId, defaultEnabled }) =>
      assertNotLocked(this.ydb)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen(() =>
          tryMutate(() => setDefaultEnabled(this.ydb, fieldId, defaultEnabled))
        )
    );
  }

  setCloudId(input: SetCloudIdInput): Result<void, SdkError> {
    return parse(setCloudIdSchema, input).andThen(({ fieldId, cloudId }) =>
      assertNotLocked(this.ydb)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen(() => tryMutate(() => setCloudId(this.ydb, fieldId, cloudId)))
    );
  }

  setType(input: SetTypeInput): Result<void, SdkError> {
    return parse(setTypeSchema, input).andThen(({ fieldId, fieldType }) =>
      assertNotLocked(this.ydb)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen(() => tryMutate(() => setFieldType(this.ydb, fieldId, fieldType)))
    );
  }

  setDependentField(input: SetDependentFieldInput): Result<void, SdkError> {
    return parse(setDependentFieldSchema, input).andThen(
      ({ fieldId, dependentFieldId }) =>
        assertNotLocked(this.ydb)
          .andThen(() => assertFieldExists(this.ydb, fieldId))
          .andThen(() => {
            if (!this.ydb.fieldsById[dependentFieldId]) {
              return err({
                type: 'STATE_ERROR' as const,
                reason: 'DEPENDENT_FIELD_NOT_FOUND' as const,
                message: `Dependent field '${dependentFieldId}' does not exist`,
              });
            }

            return tryMutate(() => setFieldId(this.ydb, fieldId, dependentFieldId));
          })
    );
  }

  setNumberFormat(input: SetNumberFormatInput): Result<void, SdkError> {
    return parse(setNumberFormatSchema, input).andThen(({ fieldId, numberFormat }) =>
      assertNotLocked(this.ydb)
        .andThen(() => assertFieldExists(this.ydb, fieldId))
        .andThen(() =>
          tryMutate(() => setFieldSettingsNumberFormat(this.ydb, fieldId, numberFormat))
        )
    );
  }

  getOrCreateSelectOption(input: GetOrCreateSelectOptionInput): Result<string, SdkError> {
    return parse(getOrCreateSelectOptionSchema, input).andThen(
      ({ fieldId, optionValue }) =>
        assertNotLocked(this.ydb)
          .andThen(() => assertFieldExists(this.ydb, fieldId))
          .andThen((field) =>
            assertFieldIsType(this.ydb, fieldId, FieldType.Select).map(() => field)
          )
          .andThen((field) => {
            const existingOption = field.selectOptions.options.find(
              (opt) => opt.label === optionValue
            );

            if (existingOption) {
              return ok(existingOption.id);
            }

            return tryMutate(() => {
              const optionId = uuidv4();

              setSelectOptions(this.ydb, fieldId, {
                type: MultiValueChangeType.APPEND,
                value: {
                  id: optionId,
                  label: optionValue,
                  color: pickNewColor(field.selectOptions.toJSON()),
                },
              });

              return optionId;
            });
          })
    );
  }

  setShowDescription(input: SetShowFieldDescriptionInput): Result<void, SdkError> {
    return parse(setShowFieldDescriptionSchema, input).andThen(
      ({ fieldId, showDescription }) =>
        assertNotLocked(this.ydb)
          .andThen(() => assertFieldExists(this.ydb, fieldId))
          .andThen(() =>
            tryMutate(() => setShowFieldDescription(this.ydb, fieldId, showDescription))
          )
    );
  }

  setPrefixOrSuffix(input: SetPrefixOrSuffixInput): Result<void, SdkError> {
    return parse(setPrefixOrSuffixSchema, input).andThen(
      ({ fieldId, changeType, newValue }) =>
        assertNotLocked(this.ydb)
          .andThen(() => assertFieldExists(this.ydb, fieldId))
          .andThen(() =>
            tryMutate(() =>
              setFieldSettingsPrefixOrSuffixField(this.ydb, {
                fieldId,
                changeType,
                newValue,
              })
            )
          )
    );
  }

  // ---------------------------------------------------------------------------
  // Queries — pure reads, no guards
  // ---------------------------------------------------------------------------

  getCloudId(fieldId: string): string | undefined {
    return this.ydb.fieldsById[fieldId]?.cloudId;
  }

  getFieldIdByName(name: string): string | undefined {
    return this.ydb.fields.find((fieldId: string) => {
      const field = this.ydb.fieldsById[fieldId];

      return field?.name === name;
    });
  }

  getFieldById(fieldId: string): DatabaseField<DatabaseFieldType> | undefined {
    const yField = this.ydb.fieldsById[fieldId];

    if (!yField) {
      return undefined;
    }

    return fieldToDatabase(fieldId, yField.toJSON());
  }

  findExistingSingleSelectField(): DatabaseField<DatabaseFieldType> | undefined {
    for (const fieldId of this.ydb.fields) {
      const field = this.getFieldById(fieldId);

      if (field && field.type === DatabaseFieldType.SELECT && !field.settings.multi) {
        return field;
      }
    }

    return undefined;
  }
}
